import {
  BarcodeCapture,
  BarcodeCaptureOverlay,
  BarcodeCaptureOverlayStyle,
  BarcodeCaptureSettings,
  Symbology,
  SymbologyDescription,
  barcodeCaptureLoader,
} from "@scandit/web-datacapture-barcode";
import {
  Camera,
  CameraSwitchControl,
  DataCaptureContext,
  DataCaptureView,
  FrameSourceState,
  RectangularViewfinder,
  RectangularViewfinderLineStyle,
  RectangularViewfinderStyle,
  configure,
} from "@scandit/web-datacapture-core";
import axios from "axios";
import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const SCANDIT_LICENSE_KEY = process.env.REACT_APP_SCANDIT_KEY;

const ScannerV2 = ({ onDetected }) => {
  const navigate = useNavigate();
  const viewRef = useRef(null);
  let barcodeCapture = null;
  let camera = null;
  let context = null;

  useEffect(() => {
    async function initializeScanner() {
      try {
        if (!SCANDIT_LICENSE_KEY) {
          console.error("Scandit license key is missing!");
          return;
        }

        const view = new DataCaptureView();
        view.connectToElement(viewRef.current);

        view.showProgressBar();
        view.setProgressBarMessage("Initializing Scanner...");

        await configure({
          licenseKey: SCANDIT_LICENSE_KEY,
          libraryLocation:
            "https://cdn.jsdelivr.net/npm/@scandit/web-datacapture-barcode@7.0.0/sdc-lib/",
          moduleLoaders: [barcodeCaptureLoader()],
        });

        view.setProgressBarPercentage(null);
        view.setProgressBarMessage("Accessing Camera...");

        context = await DataCaptureContext.create();
        await view.setContext(context);

        camera = Camera.default;
        const cameraSettings = BarcodeCapture.recommendedCameraSettings;
        await camera.applySettings(cameraSettings);
        await context.setFrameSource(camera);

        const settings = new BarcodeCaptureSettings();
        settings.enableSymbologies([
          Symbology.EAN13UPCA,
          Symbology.EAN8,
          Symbology.UPCE,
          Symbology.QR,
          Symbology.DataMatrix,
          Symbology.Code39,
          Symbology.Code128,
          Symbology.InterleavedTwoOfFive,
        ]);

        const symbologySettings = settings.settingsForSymbology(
          Symbology.Code39
        );
        symbologySettings.activeSymbolCounts = Array.from(
          { length: 14 },
          (_, i) => i + 7
        );

        barcodeCapture = await BarcodeCapture.forContext(context, settings);
        await barcodeCapture.setEnabled(false);

        view.addControl(new CameraSwitchControl());

        const barcodeCaptureOverlay =
          await BarcodeCaptureOverlay.withBarcodeCaptureForViewWithStyle(
            barcodeCapture,
            view,
            BarcodeCaptureOverlayStyle.Frame
          );

        document.body.addEventListener(
          "click",
          () => {
            navigator.vibrate?.(200);
          },
          { once: true }
        );

        barcodeCapture.addListener({
          didScan: async (barcodeCaptureMode, session) => {
            console.log("Full Scan Session:", session); // Debugging
            let scannedBarcode = session._newlyRecognizedBarcode?._data;
            console.log("Barcode Data:", scannedBarcode);

            if (scannedBarcode) {
              try {
                await barcodeCapture.setEnabled(false);
                barcodeCapture.removeAllListeners();

                await camera.switchToDesiredState(FrameSourceState.Off);

                await context.dispose();

                console.log("Scanner successfully stopped.");
              } catch (error) {
                console.error("Error stopping scanner:", error);
              }
              const referrer = document.referrer;

              if (referrer.includes("https://develop-delmonte.dird393khuadt.amplifyapp.com/")) {
                try {
                  const response = await axios.post("/dynamo_getByUPC", {
                    barcode: scannedBarcode,
                    upc: scannedBarcode,
                  });
                
                  const result = response.data || {};
                
                  console.log("API result:", result);
                
                  const message = {
                    type: "SCANNED_RESULT",
                    result,
                  };
                
                  if (window.opener) {
                    window.opener.postMessage(message, "*");
                  } else {
                    window.parent.postMessage(message, "*");
                  }
                
                  window.close();
                } catch (error) {
                  console.error("Error during external API call:", error);
                  const message = {
                    type: "SCANNED_RESULT",
                    result: {},
                  };
                
                  if (window.opener) {
                    window.opener.postMessage(message, "*");
                  } else {
                    window.parent.postMessage(message, "*");
                  }
                
                  window.close();
                }
                
              } else {
                navigate(`/products/details/${scannedBarcode}`);
              }
              return;
            }

            if (
              !session.newlyRecognizedBarcodes ||
              session.newlyRecognizedBarcodes.length === 0
            ) {
              console.warn("No barcodes detected in this scan.");
              return;
            }

            await barcodeCapture.setEnabled(false);
            await barcodeCaptureOverlay.setViewfinder(null);

            const barcode = session.newlyRecognizedBarcodes[0];
            if (!barcode || !barcode.data) {
              console.warn("Scanned barcode is empty or invalid.");
              return;
            }

            const symbology = new SymbologyDescription(barcode.symbology);
            console.log(`Scanned: ${barcode.data} (${symbology.readableName})`);
            onDetected(barcode.data);
          },
        });

        const viewfinder = new RectangularViewfinder(
          RectangularViewfinderStyle.Square,
          RectangularViewfinderLineStyle.Light
        );
        await barcodeCaptureOverlay.setViewfinder(viewfinder);

        await camera.switchToDesiredState(FrameSourceState.On);
        await barcodeCapture.setEnabled(true);

        view.hideProgressBar();
      } catch (error) {
        console.error("Scanner initialization failed:", error);
        alert("Failed to access camera. Please check your browser settings.");
        navigate("/products/")
      }
    }

    initializeScanner();

    return () => {
      stopScanner();
    };
  }, [onDetected]);

  const stopScanner = async () => {
    try {
      if (barcodeCapture) {
        await barcodeCapture.setEnabled(false);
        barcodeCapture.removeAllListeners();
      }
      if (camera) {
        await camera.switchToDesiredState(FrameSourceState.Off);
      }
      if (context) {
        await context.dispose();
      }
      console.log("Scanner successfully stopped.");
    } catch (error) {
      console.error("Error stopping scanner:", error);
    }
  };

  return <div ref={viewRef} style={{ width: "100%", height: "100%" }} />;
};

export default ScannerV2;
